/**
 * /auth/verify-email?token=... — handles the click target of the
 * verification email Better Auth sent on signup. Phase 3b of
 * ottotester#65.
 *
 * Better Auth's default verify-email handler is at /api/auth/verify-email
 * — but we want a frontend touchpoint so the user sees "verified!"
 * rather than a JSON blob. This screen posts the token via
 * `authClient.verifyEmail` (which hits the same backend endpoint) and
 * shows the appropriate state.
 *
 * States: pending (verifying...), success, error.
 *
 * Phase 5b adds auto-forward on success: Better Auth's
 * `autoSignInAfterVerification: true` means the user is signed in by
 * the time we reach the success state. We immediately fetch /api/me
 * and route to either /onboarding (no orgs yet) or their default
 * workspace's Runs page (existing org). No "Continue" click required —
 * the success card is a transitional surface, not a destination.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'
import { identityAPI, workspacesAPI } from '@/lib/api'

type Status = 'pending' | 'success' | 'error'

/** Mirror of Signup.tsx's POST_VERIFY_RETURN_TO_KEY. Kept inline
 * (not exported) because the two-screen contract is small and
 * cross-importing pure-data constants for a 30-character string
 * isn't worth the coupling. */
const POST_VERIFY_RETURN_TO_KEY = 'ottotester.postVerifyReturnTo'

function consumeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(POST_VERIFY_RETURN_TO_KEY)
    if (!value) return null
    sessionStorage.removeItem(POST_VERIFY_RETURN_TO_KEY)
    // Re-validate: must be a same-origin pathname. Defends against
    // a session-storage value that was tampered with via devtools
    // or that drifted past Signup's safeReturnTo gate somehow.
    if (!value.startsWith('/') || value.startsWith('//')) return null
    return value
  } catch {
    return null
  }
}

export function VerifyEmail() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [status, setStatus] = useState<Status>(token ? 'pending' : 'error')
  const [errorMessage, setErrorMessage] = useState<string>(
    token ? '' : 'Verification link is missing its token. The link may have been truncated by your email client.',
  )
  // Tracks whether the post-verify auto-forward fetch failed. If
  // /api/me or /api/workspaces/me throws, we stop spinning and
  // surface a clear message so the success card doesn't read as a
  // hung request.
  const [forwardFailed, setForwardFailed] = useState(false)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    void (async () => {
      const { error } = await authClient.verifyEmail({ query: { token } })
      if (cancelled) return
      if (error) {
        setStatus('error')
        setErrorMessage(error.message ?? 'Verification failed. The link may have expired.')
      } else {
        setStatus('success')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  // Auto-forward on success. Runs after the success state renders so
  // the user briefly sees the "verified" check before the redirect —
  // a hard zero-ms cut would feel jarring. The explicit fetch lets
  // us land directly on the right destination instead of bouncing
  // through "/" → IdentityGate → /onboarding for the new-user case.
  useEffect(() => {
    if (status !== 'success') return
    let cancelled = false
    void (async () => {
      try {
        const me = await identityAPI.me()
        if (cancelled) return
        if (!me) {
          // Session somehow not present despite the verify having
          // run autoSignInAfterVerification. Treat as a forward
          // failure — the user has to sign in manually.
          setForwardFailed(true)
          return
        }
        // Honor a returnTo handed off via sessionStorage (typically
        // set by Signup.tsx for the invitation flow). Trumps the
        // needsOnboarding default because the caller asked for a
        // specific destination — usually /accept-invitation/<id>,
        // which itself flips needsOnboarding to false on accept.
        const returnTo = consumeReturnTo()
        if (returnTo) {
          navigate(returnTo, { replace: true })
          return
        }
        if (me.needsOnboarding) {
          navigate('/onboarding', { replace: true })
          return
        }
        const ws = await workspacesAPI.getMe()
        if (cancelled) return
        navigate(`/workspaces/${ws._id}/results`, { replace: true })
      } catch {
        if (cancelled) return
        // Stop the spinner and surface the manual continue link as
        // the primary action. The card stays on "Email verified"
        // (the verify itself succeeded) so the user knows their
        // account is good.
        setForwardFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [status, navigate])

  if (status === 'pending') {
    return (
      <AuthLayout title="Verifying your email…">
        <div className="flex flex-col items-center gap-3 py-2" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">This usually takes a moment.</p>
        </div>
      </AuthLayout>
    )
  }

  if (status === 'success') {
    return (
      <AuthLayout
        title="Email verified"
        subtitle={forwardFailed ? 'Continue manually below.' : 'Setting up your workspace…'}
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
          {!forwardFailed && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
          )}
          {forwardFailed && (
            <p className="text-xs text-muted-foreground text-center">
              We couldn't reach the server to bring you to your workspace.
              Your account is verified — sign in to continue.
            </p>
          )}
          <Link
            to="/auth/login"
            className={
              forwardFailed
                ? 'text-sm text-primary hover:underline'
                : 'text-xs text-muted-foreground hover:text-foreground'
            }
          >
            {forwardFailed ? 'Go to sign in' : 'Not redirecting? Continue manually →'}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  // Recovery copy: signing up *again* would create a new account
  // (or fail email-uniqueness); it isn't the right action. The actual
  // path is "try to sign in — if your account exists but is unverified,
  // we'll prompt for a new verification email." That backend-prompted
  // resend lands with Phase 6's mandatory-auth wiring; until then,
  // contacting the workspace admin is the honest fallback.
  return (
    <AuthLayout title="Verification failed" subtitle={errorMessage}>
      <div className="flex flex-col items-center gap-4 py-2">
        <XCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground text-center">
          The link may have expired. Try signing in — if your account isn't verified yet, we'll resend the link from there.
        </p>
        <Link to="/auth/login" className="text-sm text-primary hover:underline">
          Go to sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
